(() => {
  if (customElements.get('hatchill-collection-browser')) return;

  class CollectionBrowser extends HTMLElement {
    connectedCallback() {
      this.abortController = null;
      this.requestId = 0;
      this.handleSubmit = this.onSubmit.bind(this);
      this.handleChange = this.onChange.bind(this);
      this.handleClick = this.onClick.bind(this);
      this.handleKey = this.onKey.bind(this);
      this.handleToggle = this.onToggle.bind(this);
      this.handleHistory = () => this.navigate(new URL(window.location.href), { history: false });
      this.addEventListener('submit', this.handleSubmit);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKey);
      this.addEventListener('toggle', this.handleToggle, true);
      window.addEventListener('popstate', this.handleHistory);
      this.classList.add('hc-enhanced');
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.requestId++;
      this.releaseDialog();
      this.removeEventListener('submit', this.handleSubmit);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKey);
      this.removeEventListener('toggle', this.handleToggle, true);
      window.removeEventListener('popstate', this.handleHistory);
    }

    formURL(form) {
      const url = new URL(form.action, window.location.href);
      url.search = '';
      for (const [key, value] of new FormData(form)) {
        if (String(value).trim() !== '') url.searchParams.append(key, value);
      }
      return url;
    }

    onSubmit(event) {
      const form = event.target.closest('.hc-tool-form');
      if (!form) return;
      event.preventDefault();
      this.closeDialog();
      this.navigate(this.formURL(form), { focusId: this.querySelector('.hc-filter-disclosure summary')?.id });
    }

    onChange(event) {
      const form = event.target.closest('[data-hc-auto]');
      if (form) this.navigate(this.formURL(form), { focusId: event.target.id });
    }

    onClick(event) {
      if (event.target.closest('[data-hc-close]')) {
        this.closeDialog();
        return;
      }
      const link = event.target.closest('[data-hc-navigate], [data-hc-pagination] a');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const destination = new URL(link.href, window.location.href);
      if (destination.origin !== window.location.origin || destination.pathname !== window.location.pathname) return;
      event.preventDefault();
      const pagination = Boolean(link.closest('[data-hc-pagination]'));
      this.closeDialog();
      this.navigate(destination, { pagination, focusId: this.querySelector('.hc-filter-disclosure summary')?.id });
    }

    onToggle(event) {
      if (!event.target.matches('.hc-filter-disclosure')) return;
      if (!event.target.open) {
        this.releaseDialog();
        return;
      }
      const panel = event.target.querySelector('.hc-filter-panel');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      if (!this.dialogLocked) {
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.dialogLocked = true;
        this.dialogInert = [];
        // Suspend siblings at each level without disabling the panel or backdrop.
        let branch = panel;
        while (branch.parentElement && branch !== document.body) {
          for (const sibling of branch.parentElement.children) {
            if (sibling === branch || sibling.matches('.hc-filter-backdrop,script,style,link')) continue;
            this.dialogInert.push([sibling, sibling.inert]);
            sibling.inert = true;
          }
          branch = branch.parentElement;
        }
      }
      panel.querySelector('button')?.focus();
    }

    onKey(event) {
      const disclosure = this.querySelector('.hc-filter-disclosure[open]');
      if (!disclosure) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = [...disclosure.querySelectorAll('.hc-filter-panel a[href], .hc-filter-panel button, .hc-filter-panel input:not([type="hidden"]), .hc-filter-panel select')]
        .filter(control => !control.disabled && control.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      const outside = !controls.includes(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || outside)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || outside)) {
        event.preventDefault();
        first?.focus();
      }
    }

    closeDialog() {
      const disclosure = this.querySelector('.hc-filter-disclosure[open]');
      if (disclosure) {
        disclosure.open = false;
        this.releaseDialog();
        disclosure.querySelector('summary')?.focus();
      }
      this.releaseDialog();
    }

    releaseDialog() {
      if (this.dialogLocked) document.body.style.overflow = this.previousOverflow;
      for (const [element, inert] of this.dialogInert || []) element.inert = inert;
      this.dialogInert = [];
      this.dialogLocked = false;
    }

    syncPageIdentity(next) {
      try {
        const list = JSON.parse(next.querySelector('script[data-hatchill-collection-schema]')?.textContent || 'null');
        if (list?.['@type'] !== 'ItemList' || typeof list['@id'] !== 'string') return;
        const canonical = new URL(list['@id']);
        if (canonical.origin !== window.location.origin || canonical.pathname !== window.location.pathname ||
            canonical.hash !== '#items' || canonical.username || canonical.password ||
            [...canonical.searchParams.keys()].some(key => key !== 'page') ||
            (canonical.searchParams.has('page') && !/^[1-9]\d*$/.test(canonical.searchParams.get('page')))) return;
        canonical.hash = '';
        const script = document.querySelector('script[data-hatchill-site-schema]');
        if (!script) return;
        const graph = JSON.parse(script.textContent);
        if (!Array.isArray(graph?.['@graph'])) return;
        const page = graph['@graph'].find(node => node['@type'] === 'CollectionPage');
        if (!page || new URL(page.url).origin !== canonical.origin || new URL(page.url).pathname !== canonical.pathname) return;
        page.url = canonical.href;
        page['@id'] = canonical.href + '#webpage';
        page.mainEntity = { '@id': list['@id'] };
        if (typeof list.name === 'string' && list.name.trim()) page.name = list.name;
        script.textContent = JSON.stringify(graph).replace(/</g, '\\u003c');
        document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical.href);
      } catch {
        // Optional metadata must not interrupt a valid shopping response.
      }
    }

    async navigate(destination, { history = true, focusId, pagination = false } = {}) {
      if (destination.origin !== window.location.origin || destination.pathname !== window.location.pathname) return;
      this.abortController?.abort();
      this.abortController = new AbortController();
      const id = ++this.requestId;
      const sectionURL = new URL(destination.href);
      sectionURL.searchParams.set('section_id', this.dataset.sectionId);
      this.setAttribute('aria-busy', 'true');
      const oldError = this.querySelector('[data-hc-error]');
      if (oldError) oldError.hidden = true;
      try {
        const response = await fetch(sectionURL, { signal: this.abortController.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = html.querySelector('hatchill-collection-browser');
        if (!next || next.dataset.sectionId !== this.dataset.sectionId || !next.querySelector('#ProductGridContainer')) {
          throw new Error('Collection section missing');
        }
        if (id !== this.requestId || !this.isConnected) return;
        this.releaseDialog();
        this.innerHTML = next.innerHTML;
        this.syncPageIdentity(next);
        this.querySelectorAll('.scroll-trigger').forEach(element => element.classList.add('scroll-trigger--cancel'));
        if (history) window.history.pushState({ hcCollection: true }, '', destination.href);
        if (pagination) {
          const grid = this.querySelector('#ProductGridContainer');
          grid.setAttribute('tabindex', '-1');
          grid.focus({ preventScroll: true });
          grid.scrollIntoView({ block: 'start', behavior: 'instant' });
        } else if (focusId) {
          this.querySelector(`[id="${CSS.escape(focusId)}"]`)?.focus({ preventScroll: true });
        }
        if (typeof window.initMoreSwatchButtons === 'function') window.initMoreSwatchButtons(this);
        this.dispatchEvent(new CustomEvent('hatchill:collection-updated', { bubbles: true }));
      } catch (error) {
        if (error.name === 'AbortError' || id !== this.requestId || !this.isConnected) return;
        const errorMessage = this.querySelector('[data-hc-error]');
        const retry = this.querySelector('[data-hc-retry]');
        if (retry) retry.href = destination.href;
        if (errorMessage) errorMessage.hidden = false;
      } finally {
        if (id === this.requestId) this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('hatchill-collection-browser', CollectionBrowser);
})();
